import {ListViewFilterElementType} from "../../../enums/listViews/listViewFilterElementType";
import {ListViewFilterControlElementType} from "../../../enums/listViews/listViewFilterControlElementType";
import {ListViewFilterLayoutElementType} from "../../../enums/listViews/listViewFilterLayoutElementType";
import {ListViewFilterElementCapability} from "./listViewFilterElementCapability";
import assertNever from "../../../../global/assetNever";
import {EnumHelper} from "../../../helpers/enumHelper";
import ListViewFilterElement from "./listViewFilterElement";
import {VerticalAlignment} from "../../../enums/verticalAlignment";
import ListViewFilterElementDefinition from "./listViewFilterElementDefinition";
import {SearchMatchType} from "~/enums/searchMatchType";

export default class ListViewFilterElementCapabilityManager {
  static debug: boolean = false;

  static SELECTABLE_ELEMENTS: ListViewFilterElementType[] = [
    ...EnumHelper.toArray(ListViewFilterControlElementType),
    ...EnumHelper.toArray(ListViewFilterLayoutElementType),
  ];

  static LABELABLE_ELEMENTS: ListViewFilterElementType[] = [
    ListViewFilterControlElementType.Dropdown,
    ListViewFilterControlElementType.SearchBar,
    ListViewFilterControlElementType.DateRange,
    ListViewFilterControlElementType.Toggle,
  ];

  static PLACEHOLDERABLE_ELEMENTS: ListViewFilterElementType[] = [
    ListViewFilterControlElementType.Dropdown,
    ListViewFilterControlElementType.SearchBar,
  ];

  static PROPERTY_SEARCHABLE_ELEMENTS: ListViewFilterElementType[] = [
    ListViewFilterControlElementType.SearchBar,
  ];

  static PROPERTY_DATETIME_FILTERABLE_ELEMENTS: ListViewFilterElementType[] = [
    ListViewFilterControlElementType.DateRange,
  ];

  static PROPERTY_DROPDOWN_FILTERABLE_ELEMENTS: ListViewFilterElementType[] = [
    ListViewFilterControlElementType.Dropdown,
  ];

  static DATETIMEABLE: ListViewFilterElementType[] = [
    ListViewFilterControlElementType.DateRange,
  ];

  static DEFAULTABLE: ListViewFilterElementType[] = [
    ListViewFilterControlElementType.Toggle,
  ];

  static CONDITIONABLE: ListViewFilterElementType[] = [
    ListViewFilterControlElementType.Toggle,
  ];

  static CONTAINERABLE: ListViewFilterElementType[] = [
    ListViewFilterLayoutElementType.Grid,
    ListViewFilterLayoutElementType.Stack,
  ];

  static VERTICAL_ALIGNABLE: ListViewFilterElementType[] = [
    ListViewFilterLayoutElementType.Grid,
  ];

  static COLUMN_LAYOUTABLE: ListViewFilterElementType[] = [
    ListViewFilterLayoutElementType.Grid,
  ];

  static HELPTEXTABLE: ListViewFilterElementType[] = [
    ListViewFilterLayoutElementType.Grid,
    ListViewFilterLayoutElementType.Stack,
  ];

  static can(type: ListViewFilterElementType, duty: ListViewFilterElementCapability): boolean {
    let can: boolean = false;
    switch (duty) {
      case ListViewFilterElementCapability.selectable:
        can = this.SELECTABLE_ELEMENTS.includes(type);
        break;
      case ListViewFilterElementCapability.labelable:
        can = this.LABELABLE_ELEMENTS.includes(type);
        break;
      case ListViewFilterElementCapability.placeholderable:
        can = this.PLACEHOLDERABLE_ELEMENTS.includes(type);
        break;
      case ListViewFilterElementCapability.propertySearchable:
        can = this.PROPERTY_SEARCHABLE_ELEMENTS.includes(type);
        break;
      case ListViewFilterElementCapability.propertyDateTimeFilterable:
        can = this.PROPERTY_DATETIME_FILTERABLE_ELEMENTS.includes(type);
        break;
      case ListViewFilterElementCapability.propertyDropdownFilterable:
        can = this.PROPERTY_DROPDOWN_FILTERABLE_ELEMENTS.includes(type);
        break;
      case ListViewFilterElementCapability.datetimeable:
        can = this.DATETIMEABLE.includes(type);
        break;
      case ListViewFilterElementCapability.defaultable:
        can = this.DEFAULTABLE.includes(type);
        break;
      case ListViewFilterElementCapability.conditionable:
        can = this.CONDITIONABLE.includes(type);
        break;
      case ListViewFilterElementCapability.containerable:
        can = this.CONTAINERABLE.includes(type);
        break;
      case ListViewFilterElementCapability.verticalAlignable:
        can = this.VERTICAL_ALIGNABLE.includes(type);
        break;
      case ListViewFilterElementCapability.columnLayoutable:
        can = this.COLUMN_LAYOUTABLE.includes(type);
        break;
      case ListViewFilterElementCapability.helpTextable:
        can = this.HELPTEXTABLE.includes(type);
        break;
      default:
        assertNever(duty);
        can = false;
        break;
    }
    this.log(`Type ${ListViewFilterControlElementType[type]} ${can ? 'can' : 'can\'t'} ${ListViewFilterElementCapability[duty]}`);
    return can;
  }

  static addDefaultProperties(m: ListViewFilterElement): void {
    // done this way so a case statement can use assertNever to ensure all capabilities are accounted for
    EnumHelper.toArray(ListViewFilterElementCapability).forEach((c: ListViewFilterElementCapability) => {
      switch (c) {
        case ListViewFilterElementCapability.labelable:
          const defaultLabel = m.is(ListViewFilterControlElementType.SearchBar) ? 'Search' : null;
          m.defineProperty('label_title', defaultLabel);
          break;
        case ListViewFilterElementCapability.placeholderable:
          const defaultPlaceholder = m.is(ListViewFilterControlElementType.SearchBar) ? 'Search...' : null;
          m.defineProperty('placeholder_text', defaultPlaceholder);
          break;
        case ListViewFilterElementCapability.propertySearchable:
          m.defineProperty('match_type', SearchMatchType.CloseVariant);
          break;
        case ListViewFilterElementCapability.propertyDropdownFilterable:
          m.defineProperty('property_to_filter_is_default', false);
          m.defineProperty('property_to_filter_default_type', null);
          m.defineProperty('property_to_filter_id', null);
          m.defineProperty('default_property_filter_option_id', ListViewFilterElementDefinition.getDefaultPropertyFilterOptionId(m.filter_type));
          m.defineProperty('default_property_filter_option_ids', []);
          break;
        case ListViewFilterElementCapability.propertyDateTimeFilterable:
          // Do nothing, handled in ListViewFilterElementCapability.propertyDropdownFilterable case
          // if needs between these two cases ever diverge, defineProperty as needed
          break;
        case ListViewFilterElementCapability.datetimeable:
          m.defineProperty('start_datetime', null);
          m.defineProperty('end_datetime', null);
          break;
        case ListViewFilterElementCapability.defaultable:
          const defaultValue = m.is(ListViewFilterControlElementType.Toggle) ? true : null;
          m.defineProperty('default_value', defaultValue);
          break;
        case ListViewFilterElementCapability.verticalAlignable:
          const defaultVerticalAlignment = m.is(ListViewFilterLayoutElementType.Grid) ? VerticalAlignment.Bottom : null;
          m.defineProperty('vertical_alignment', defaultVerticalAlignment);
          break;
        case ListViewFilterElementCapability.columnLayoutable:
          const defaultLayoutConfig = m.is(ListViewFilterLayoutElementType.Grid) ? [1, 1] : null;
          m.defineProperty('layout_config', defaultLayoutConfig);
          break;
        case ListViewFilterElementCapability.helpTextable:
          m.defineProperty('help_text', null);
          break;
        case ListViewFilterElementCapability.conditionable:
          // Do nothing, handled in ListViewFilterElement.prototype.initialize
          break;
        case ListViewFilterElementCapability.containerable:
        case ListViewFilterElementCapability.selectable:
          // Do nothing
          break;
        default:
          assertNever(c);
          break;
      }
    });
  }

  protected static log(message: string) {
    if (this.debug) {
      console.log(`DEBUG: ListViewFilterElementCapabilityManager: ${message}`);
    }
  }
};